import * as React from "react";
import { DmsCaseInfo, iManageUserAuthInfo, LinkedStorageInfo, NetDocumentsUserAuthInfo } from "../models";
import "./LocationItem.css";
import {get, hostserver} from "../api";
import {MessageBar, MessageBarType, PrimaryButton, Spinner} from "office-ui-fabric-react";
import * as microsoftTeams from "@microsoft/teams-js";
import { ErrorHandler } from "../errors/ErrorHandler";

export interface IAuthenticateUserProps {
    userId: string;
    tenantId: string;
    teamGroupId: string;
    channelId: string;
    AuthenticatedWithCase: (storageType: string, caseInfo: DmsCaseInfo, externalTeamId: string) => void;
}

export interface IAuthenticateUserState {
    authenticated: boolean;
    initalizing: boolean;
    imanageUsesOAuth: boolean;
    storageType: string;
    spAuthRequired: boolean;
    authDeclined: boolean;

    caseInfo: DmsCaseInfo | null;
    caseInfoLoading: boolean;

    error: string;
    spHostKVPrefix: string;
    library: string;
}

const getFormattedStorageType = (type: string) => {
  const mapping: Record<string, string> = {
    imanage: "iManage",
    netdocuments: "NetDocuments",
  };

  return mapping[type.toLowerCase()] || type;
};

export default class AuthenticateUser extends React.Component<IAuthenticateUserProps, IAuthenticateUserState> {
    errorHandler = new ErrorHandler();

    constructor(props: IAuthenticateUserProps) {
        super(props);
        this.state = {
            authenticated: false,
            initalizing: true,
            imanageUsesOAuth: false,
            storageType: "",
            spAuthRequired: false,
            authDeclined: false,
            caseInfo: null,
            caseInfoLoading: true,
            error: "",
            spHostKVPrefix: "",
            library: "",
        };
    }

    public async componentWillMount(): Promise<void> {
        await this.initializeTeamsSdk();

        await this.startInitializationProcess();
    }

    public async componentDidUpdate(prevProps: IAuthenticateUserProps, prevState: IAuthenticateUserState) {
        if (prevState.authenticated === false && this.state.authenticated === true) {
            // after the user has authenticated, load the sp list details. This call requires the user to be authenticated already.

            let externalTeamId = "";
            await get(
                "/api/teamsdmsintegration/GetExternalTeamIdFromSPList?teamId=" +
                    this.props.teamGroupId +
                    "&channelId=" +
                    this.props.channelId +
                    "&tenantId=" +
                    this.props.tenantId
            )
                .then((response) => response.json())
                .then((response) => {
                    const responseTyped = response as string;
                    externalTeamId = responseTyped;
                });

            await get(
                "/api/teamsdmsintegration/GetDmsCaseFromSPListExternal?teamId=" +
                    this.props.teamGroupId +
                    "&tenantId=" +
                    this.props.tenantId +
                    "&userId=" +
                    this.props.userId +
                    "&channelId=" +
                    this.props.channelId +
                    "&useServiceCredentials=true"
            )
                .then((r) => {
                    if (r.status.toString().startsWith("2") || r.status.toString().startsWith("404")) {
                        return r.json();
                    }
                    this.setState({caseInfoLoading: false});
                    throw new Error("Unable to get linked case details");
                })
                .then((j) => {
                    if (j.Error !== undefined && j.Error !== null) {
                        this.errorHandler.HandleConfigurationErrorResponse(j, "Unable to get linked case details", (errorMessage: string) => {
                            this.setState({ caseInfoLoading: false, error: errorMessage });
                        });
                    } else {
                        const caseInfo = j as DmsCaseInfo;
                        this.setState({caseInfo, caseInfoLoading: false});
                        this.props.AuthenticatedWithCase(this.state.storageType, caseInfo, externalTeamId);
                    }
                });
        }
    }

    public render() {
        if (this.state.spAuthRequired) {
            // Show the login button
            return (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    isMultiline={false}
                    actions={
                        <div>
                            <PrimaryButton onClick={this.doSPLogin.bind(this)} text={"Log In"} />
                        </div>
                    }
                >
                    You are not currently logged in. Log in to access file content.
                </MessageBar>
            );
        }
        if (this.state.authDeclined) {
            return (
                <div>
                    <div>{getFormattedStorageType(this.state.storageType)} authentication denied.</div>
                    <br></br>
                    <PrimaryButton onClick={() => this.checkAuthenticatedToDMS()} text={"Authenticate"} />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="center" style={{color: "red"}}>
                    {this.state.error}
                </div>
            );
        }

        if (this.state.initalizing) {
            return <Spinner />;
        }

        if (this.state.caseInfo == null && this.state.authenticated) {
            if (this.state.caseInfoLoading) {
                return <Spinner />;
            } else {
                return <div>No case is connected</div>;
            }
        }

        return null;
    }

    private startInitializationProcess = async () => {
        this.setState({spAuthRequired: false});

        await get(
            "/api/teamsdmsintegration/AuthenticatedToGraphAndSharepoint?userId=" +
                this.props.userId +
                "&tenantId=" +
                this.props.tenantId +
                "&teamsId=" +
                this.props.teamGroupId
        )
            .then((response) => response.json())
            .then(async (response) => {
                const authenticated = response as boolean;
                if (!authenticated) {
                    this.setState({spAuthRequired: true});
                } else {
                    await get(
                        "/api/teamsdmsintegration/GetLinkedStorageTypeExternal?teamId=" +
                            this.props.teamGroupId +
                            "&tenantId=" +
                            this.props.tenantId +
                            "&userId=" +
                            this.props.userId
                    )
                        .then((r) => {
                            if (r.status.toString().startsWith("2") || r.status.toString().startsWith("404")) {
                                return r.json();
                            }
                            throw new Error("Unable to get linked case details");
                        })
                        .then((j) => {
                            if (j.Error !== undefined && j.Error !== null) {
                                this.errorHandler.HandleConfigurationErrorResponse(j, "Unable to get linked case details", (errorMessage: string) => {
                                    this.setState({ caseInfoLoading: false, error: errorMessage });
                                });
                            } else {
                                const storageInfo = j as LinkedStorageInfo;
                                this.setState({
                                    storageType: storageInfo.StorageType,
                                    spHostKVPrefix: storageInfo.SPHostKVPrefix,
                                    spAuthRequired: false,
                                    library: storageInfo.SiteUrl,
                                });
                                if (storageInfo != null) {
                                    this.checkAuthenticatedToDMS();
                                } else {
                                    this.setState({authenticated: true, initalizing: false});
                                }
                            }
                        });
                }
            })
            .catch((reason) => {
                if (reason.message) {
                    if (reason.message == "n is null") {
                        this.setState({
                            initalizing: false,
                            error: "Content Connector is not installed or has not been set up correctly.",
                        });
                    } else {
                        this.setState({initalizing: false, error: reason.message});
                    }
                } else {
                    this.setState({initalizing: false, error: "Failed to authenticate."});
                }
            });
    };

    private async initializeTeamsSdk() {
        await microsoftTeams.app.initialize();
    }

    private async authenticateWithRetry(authUrl: string, retries = 3, delay = 1000) {
        const authenticateParameters = {
            url: `${hostserver()}/api/teamsdmsintegration/RedirectToAuth?url=${authUrl}&tenantId=${this.props.tenantId}&spHostKvPrefix=${this.state.spHostKVPrefix}&storageType=${this.state.storageType}`,
            width: 600,
            height: 535,
            successCallback: function (_: string) {
                this.setState({ authenticated: true, initializing: false });
            }.bind(this),
            failureCallback: function (_: string) {
                this.setState({ authDeclined: true });
            }.bind(this)
        };

        await this.initializeTeamsSdk();

        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                await microsoftTeams.authentication.authenticate(authenticateParameters);
                return;
            } catch (error) {
                if (attempt < retries) {
                    const retryDelay = delay * Math.pow(2, attempt - 1); // Exponential backoff
                    await new Promise(res => setTimeout(res, retryDelay));
                }
            }
        }
    }

    private async checkAuthenticatedToDMS() {
        let authenticatedToImanage = false;

        this.setState({authDeclined: false});

        if (this.state.storageType === "imanage") {
            await get(
                "/api/teamsdmsintegration/AuthenticatedToImanage?userId=" +
                    this.props.userId +
                    "&tenantId=" +
                    this.props.tenantId +
                    "&spHostKVPrefix=" +
                    this.state.spHostKVPrefix +
                    "&library=" +
                    this.state.library +
                    "&teamsId=" +
                    this.props.teamGroupId
            )
                .then((response) => response.json())
                .then(async (response) => {
                    if (response.Error !== undefined && response.Error !== null) {
                        const error = JSON.parse(response.Error);
                        if (error.Error !== undefined && error.Error !== null) {
                            this.errorHandler.HandleConfigurationErrorResponse(error, "Unable to get linked case details", (errorMessage: string) => {
                                this.setState({ caseInfoLoading: false, error: errorMessage });
                            });
                        }
                    }

                    const iManageAuthInfo = response as iManageUserAuthInfo;
                    authenticatedToImanage = iManageAuthInfo.IsAuthenticated;

                    if (!authenticatedToImanage && iManageAuthInfo.AuthenticationUrl) {
                        const authUrl = encodeURIComponent(iManageAuthInfo.AuthenticationUrl);
                        await this.authenticateWithRetry(authUrl);
                    } else {
                        this.setState(
                            {
                                authenticated: authenticatedToImanage,
                                initalizing: false,
                                imanageUsesOAuth: !!iManageAuthInfo.AuthenticationUrl,
                            },
                            () => {
                                if (authenticatedToImanage) {
                                    this.setState({authenticated: true, initalizing: false});
                                }
                            }
                        );
                    }
                });
        } else if (this.state.storageType === "netdocuments") {
            await get(
                "/api/teamsdmsintegration/AuthenticatedToNetDocuments?userId=" +
                    this.props.userId +
                    "&tenantId=" +
                    this.props.tenantId +
                    "&spHostKVPrefix=" +
                    this.state.spHostKVPrefix +
                    "&teamsId=" +
                    this.props.teamGroupId
            )
                .then((response) => response.json())
                .then(async (response) => {
                    const netDocsUserAuthInfo = response as NetDocumentsUserAuthInfo;

                    if (!netDocsUserAuthInfo.IsAuthenticated) {
                        const authUrl = encodeURIComponent(netDocsUserAuthInfo.AuthenticationUrl);
                        await this.authenticateWithRetry(authUrl);
                    } else {
                        this.setState({authenticated: true, initalizing: false});
                    }
                });
        } else if (this.state.storageType === "sharepoint") {
            this.setState({authenticated: true, initalizing: false});
        }
    }

    private doSPLogin() {
        const loginUrl = hostserver() + `/DMSConnector/StartUserLoginFlow?tenantId=${this.props.tenantId}`;

        microsoftTeams.authentication.authenticate({
            url: loginUrl,
            width: 600,
            height: 535,
            successCallback: function (_: string) {
                this.startInitializationProcess();
            }.bind(this),
            failureCallback: function (reason: string) {
                this.setState({spAuthRequired: false, error: reason, initalizing: false});
            }.bind(this),
        });
    }
}
